/**
 * Resume-safe poller for DTSE transcript ingest jobs (User Research Synthesis +
 * Synthesis Visualizations). The worker keeps going if the consultant leaves the
 * page; this hook reattaches via sessionStorage and GET /ingest/latest.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api';

export type IngestJobState =
  | 'idle'
  | 'uploading'
  | 'queued'
  | 'active'
  | 'waiting'
  | 'delayed'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'unknown';

export interface IngestProgressDetail {
  current?: number;
  total?: number;
  filename?: string;
  pct?: number;
  phase?: string;
  active?: string[];
}

export interface IngestJobSnapshot {
  jobId: string | null;
  state: IngestJobState;
  progress: number;
  progressDetail: IngestProgressDetail | null;
  files: string[];
  ingested: number | null;
  ingestErrors: string[];
  ingestSkipped: string[];
  skipFiles: string[];
  error: string | null;
  /** True when this hook instance watched the job go from in-flight to terminal. */
  justFinished?: boolean;
}

const POLL_MS = 3000;
const MAX_POLLS = 400;
const BATCH_SIZE = 80;

const storageKey = (projectId: number | string) => `urs_ingest_job_${projectId}`;

function readStoredJobId(projectId: number | string): string | null {
  if (typeof window === 'undefined') return null;
  try { return window.sessionStorage.getItem(storageKey(projectId)); } catch { return null; }
}

function writeStoredJobId(projectId: number | string, jobId: string | null) {
  if (typeof window === 'undefined') return;
  try {
    if (jobId) window.sessionStorage.setItem(storageKey(projectId), jobId);
    else window.sessionStorage.removeItem(storageKey(projectId));
  } catch { /* sessionStorage unavailable */ }
}

function emptySnapshot(): IngestJobSnapshot {
  return {
    jobId: null,
    state: 'idle',
    progress: 0,
    progressDetail: null,
    files: [],
    ingested: null,
    ingestErrors: [],
    ingestSkipped: [],
    skipFiles: [],
    error: null,
  };
}

function fromApi(data: any): IngestJobSnapshot {
  const state = (data?.state || 'unknown') as IngestJobState;
  return {
    jobId: data?.jobId ? String(data.jobId) : null,
    state,
    progress: typeof data?.progress === 'number' ? data.progress : 0,
    progressDetail: data?.progressDetail && typeof data.progressDetail === 'object' ? data.progressDetail : null,
    files: Array.isArray(data?.files) ? data.files : [],
    ingested: typeof data?.ingested === 'number' ? data.ingested : null,
    ingestErrors: Array.isArray(data?.ingestErrors) ? data.ingestErrors : [],
    ingestSkipped: Array.isArray(data?.ingestSkipped) ? data.ingestSkipped : [],
    skipFiles: Array.isArray(data?.skipFiles) ? data.skipFiles : [],
    error: data?.error || null,
  };
}

const IN_FLIGHT: IngestJobState[] = ['queued', 'active', 'waiting', 'delayed', 'uploading'];

export function isIngestInFlight(state: IngestJobState): boolean {
  return IN_FLIGHT.includes(state);
}

export interface UseProjectIngestJob {
  snapshot: IngestJobSnapshot;
  ingesting: boolean;
  startFiles: (projectId: number | string, files: File[]) => Promise<IngestJobSnapshot>;
  startFromDocuments: (projectId: number | string, documentIds: number[]) => Promise<IngestJobSnapshot>;
  resume: (projectId: number | string) => Promise<IngestJobSnapshot | null>;
  cancel: (projectId: number | string) => Promise<void>;
  skipFile: (projectId: number | string, filename: string) => Promise<void>;
  reset: () => void;
}

export function useProjectIngestJob(): UseProjectIngestJob {
  const [snapshot, setSnapshot] = useState<IngestJobSnapshot>(emptySnapshot);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollCount = useRef(0);
  const projectIdRef = useRef<number | string | null>(null);
  const jobIdRef = useRef<string | null>(null);
  const cancelledRef = useRef(false);
  const watchedInFlight = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const apply = useCallback((next: IngestJobSnapshot) => {
    jobIdRef.current = next.jobId;
    const flying = next.state === 'uploading' || isIngestInFlight(next.state);
    if (flying) watchedInFlight.current = true;
    const justFinished = !flying && watchedInFlight.current;
    if (justFinished) watchedInFlight.current = false;
    const tagged = { ...next, justFinished };
    setSnapshot(tagged);
    if (projectIdRef.current != null) {
      writeStoredJobId(projectIdRef.current, flying ? next.jobId : null);
    }
  }, []);

  const pollOnce = useCallback(async (projectId: number | string, jobId: string): Promise<IngestJobSnapshot> => {
    const pid = encodeURIComponent(String(projectId));
    const res = await api.get(`/api/synthesis-viz/${pid}/run/${encodeURIComponent(jobId)}`);
    return fromApi(res.data);
  }, []);

  const pollUntilDone = useCallback((projectId: number | string, jobId: string) => {
    stopPolling();
    pollCount.current = 0;
    cancelledRef.current = false;
    const tick = async () => {
      if (cancelledRef.current) return;
      pollCount.current += 1;
      try {
        const snap = await pollOnce(projectId, jobId);
        if (cancelledRef.current) return;
        apply(snap);
        if (isIngestInFlight(snap.state) && pollCount.current < MAX_POLLS) {
          pollTimer.current = setTimeout(tick, POLL_MS);
        } else if (isIngestInFlight(snap.state) && pollCount.current >= MAX_POLLS) {
          apply({
            ...snap,
            error: 'Ingestion is taking longer than expected — the run may still finish. Check back shortly.',
          });
        }
      } catch {
        if (cancelledRef.current) return;
        if (pollCount.current < MAX_POLLS) pollTimer.current = setTimeout(tick, POLL_MS);
      }
    };
    tick();
  }, [apply, pollOnce, stopPolling]);

  const enqueueFiles = useCallback(async (projectId: number | string, files: File[]): Promise<IngestJobSnapshot> => {
    const pid = encodeURIComponent(String(projectId));
    let last: IngestJobSnapshot = emptySnapshot();
    for (let i = 0; i < files.length; i += BATCH_SIZE) {
      const chunk = files.slice(i, i + BATCH_SIZE);
      const fd = new FormData();
      chunk.forEach((f) => fd.append('transcripts', f));
      const res = await api.post(`/api/synthesis-viz/${pid}/ingest`, fd);
      const jobId = res?.data?.jobId ? String(res.data.jobId) : null;
      if (!jobId) throw new Error('Upload did not start.');
      last = {
        jobId,
        state: 'queued',
        progress: 0,
        progressDetail: { current: 1, total: chunk.length, filename: chunk[0]?.name, pct: 0, phase: 'reading' },
        files: chunk.map((f) => f.name),
        ingested: null,
        ingestErrors: [],
        ingestSkipped: [],
        skipFiles: [],
        error: null,
      };
      apply(last);
      writeStoredJobId(projectId, jobId);
      // Wait for this chunk before enqueueing the next so we do not pile 100-file jobs
      // that the user cannot tell apart. Poll inline until this chunk is terminal.
      for (let n = 0; n < MAX_POLLS; n += 1) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        if (cancelledRef.current) return last;
        try { last = await pollOnce(projectId, jobId); } catch { continue; }
        apply(last);
        if (!isIngestInFlight(last.state)) break;
      }
    }
    return last;
  }, [apply, pollOnce]);

  const startFiles = useCallback(async (projectId: number | string, files: File[]) => {
    if (!files.length) return emptySnapshot();
    projectIdRef.current = projectId;
    cancelledRef.current = false;
    apply({ ...emptySnapshot(), state: 'uploading', files: files.map((f) => f.name) });
    try {
      const last = await enqueueFiles(projectId, files);
      if (isIngestInFlight(last.state) && last.jobId) pollUntilDone(projectId, last.jobId);
      return last;
    } catch (err: any) {
      const failed: IngestJobSnapshot = {
        ...emptySnapshot(),
        state: 'failed',
        error: err?.response?.data?.error || err?.message || 'Upload failed.',
      };
      apply(failed);
      return failed;
    }
  }, [apply, enqueueFiles, pollUntilDone]);

  const startFromDocuments = useCallback(async (projectId: number | string, documentIds: number[]) => {
    if (!documentIds.length) return emptySnapshot();
    projectIdRef.current = projectId;
    cancelledRef.current = false;
    apply({ ...emptySnapshot(), state: 'uploading' });
    try {
      const pid = encodeURIComponent(String(projectId));
      const res = await api.post(`/api/synthesis-viz/${pid}/ingest-from-documents`, { documentIds });
      const jobId = res?.data?.jobId ? String(res.data.jobId) : null;
      if (!jobId) throw new Error('Upload did not start.');
      const next: IngestJobSnapshot = {
        jobId,
        state: 'queued',
        progress: 0,
        progressDetail: null,
        files: [],
        ingested: null,
        ingestErrors: [],
        ingestSkipped: Array.isArray(res.data?.skipped) ? res.data.skipped : [],
        skipFiles: [],
        error: null,
      };
      apply(next);
      writeStoredJobId(projectId, jobId);
      pollUntilDone(projectId, jobId);
      return next;
    } catch (err: any) {
      const failed: IngestJobSnapshot = {
        ...emptySnapshot(),
        state: 'failed',
        error: err?.response?.data?.error || err?.message || 'Could not ingest those documents.',
      };
      apply(failed);
      return failed;
    }
  }, [apply, pollUntilDone]);

  const resume = useCallback(async (projectId: number | string) => {
    projectIdRef.current = projectId;
    const pid = encodeURIComponent(String(projectId));
    let jobId = readStoredJobId(projectId);
    try {
      if (!jobId) {
        const latest = await api.get(`/api/synthesis-viz/${pid}/ingest/latest`);
        if (!latest.data || latest.data.state === 'none') return null;
        const snap = fromApi(latest.data);
        apply(snap);
        if (snap.jobId && isIngestInFlight(snap.state)) {
          writeStoredJobId(projectId, snap.jobId);
          pollUntilDone(projectId, snap.jobId);
        }
        return snap;
      }
      const snap = await pollOnce(projectId, jobId);
      apply(snap);
      if (isIngestInFlight(snap.state)) pollUntilDone(projectId, jobId);
      else if (snap.state === 'unknown') {
        writeStoredJobId(projectId, null);
        const latest = await api.get(`/api/synthesis-viz/${pid}/ingest/latest`);
        if (!latest.data || latest.data.state === 'none') return null;
        const next = fromApi(latest.data);
        apply(next);
        if (next.jobId && isIngestInFlight(next.state)) pollUntilDone(projectId, next.jobId);
        return next;
      }
      return snap;
    } catch {
      return null;
    }
  }, [apply, pollOnce, pollUntilDone]);

  const cancel = useCallback(async (projectId: number | string) => {
    const jobId = jobIdRef.current;
    if (!jobId) return;
    const pid = encodeURIComponent(String(projectId));
    try {
      await api.delete(`/api/synthesis-viz/${pid}/run/${encodeURIComponent(jobId)}`);
    } finally {
      // Active jobs stop at the next file; keep polling so the tile shows cancelled.
      pollUntilDone(projectId, jobId);
    }
  }, [pollUntilDone]);

  const skipFile = useCallback(async (projectId: number | string, filename: string) => {
    const jobId = jobIdRef.current;
    if (!jobId || !filename) return;
    const pid = encodeURIComponent(String(projectId));
    try {
      const res = await api.post(
        `/api/synthesis-viz/${pid}/run/${encodeURIComponent(jobId)}/skip`,
        { filename },
      );
      const skipFiles = Array.isArray(res.data?.skipFiles) ? res.data.skipFiles : [filename];
      setSnapshot((prev) => ({ ...prev, skipFiles }));
    } catch {
      setSnapshot((prev) => ({
        ...prev,
        skipFiles: Array.from(new Set([...(prev.skipFiles || []), filename])),
      }));
    }
  }, []);

  const reset = useCallback(() => {
    cancelledRef.current = true;
    stopPolling();
    apply(emptySnapshot());
  }, [apply, stopPolling]);

  useEffect(() => () => {
    cancelledRef.current = true;
    stopPolling();
  }, [stopPolling]);

  return {
    snapshot,
    ingesting: snapshot.state === 'uploading' || isIngestInFlight(snapshot.state),
    startFiles,
    startFromDocuments,
    resume,
    cancel,
    skipFile,
    reset,
  };
}
